// S4 (v0.15.0) — Invite User via token flow.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useInviteUser, useRoles } from "@/api/users";
import type { Role } from "@/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function UserInvitePage() {
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  // U1a-3b — the legacy users.role the invitee lands with (drives base
  // visibility: "admin" → full tenant access). Fixed DB enum, not a tenant
  // role list. Default "viewer" (least privilege).
  const [legacyRole, setLegacyRole] = useState<Role>("viewer");
  const [entityId, setEntityId] = useState("");
  const [scope, setScope] = useState<"subtree" | "exact">("subtree");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    email: string;
    expires_at: string;
    accept_url_hint: string;
  } | null>(null);
  const invite = useInviteUser();
  const { data: roles = [] } = useRoles();
  const nav = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Enter a valid email address");
      return;
    }
    if (!roleId) {
      setError("Pick a role");
      return;
    }
    try {
      const result = await invite.mutateAsync({
        email: email.trim(),
        role_id: roleId,
        legacy_role: legacyRole,
        entity_id: entityId || null,
        scope,
      });
      setSuccess({
        email: result.email,
        expires_at: result.expires_at,
        accept_url_hint: result.accept_url_hint,
      });
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      if (typeof detail === "string" && detail.includes("already exists")) {
        setError("This email is already a member.");
      } else {
        setError(
          typeof detail === "string" ? detail : "Failed to send invitation",
        );
      }
    }
  }

  if (success) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Card className="border-emerald-500/30">
          <CardContent className="space-y-3 p-4">
            <p className="text-base font-semibold text-emerald-700">
              Invitation sent to {success.email}
            </p>
            <p className="text-sm text-muted-foreground">
              Link expires{" "}
              {new Date(success.expires_at).toLocaleString()}.
            </p>
            <p className="rounded bg-muted/40 p-2 font-mono text-xs">
              {success.accept_url_hint}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSuccess(null);
                  setEmail("");
                  setEntityId("");
                  setScope("subtree");
                }}
              >
                Invite Another
              </Button>
              <Button onClick={() => nav("/users")}>Back to Users</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Invite User
        </h1>
        <p className="text-sm text-muted-foreground">
          Invitee receives a one-time link that expires in 24 hours.
        </p>
      </div>
      <Card>
        <CardContent className="p-4">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div>
              <Label>Role</Label>
              <select
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                required
                className="h-9 w-full rounded border border-border bg-background px-2 text-sm"
              >
                <option value="">Choose a role…</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} {r.is_system_role ? "· System" : "· Custom"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Base access level</Label>
              <select
                value={legacyRole}
                onChange={(e) => setLegacyRole(e.target.value as Role)}
                className="h-9 w-full rounded border border-border bg-background px-2 text-sm"
              >
                <option value="viewer">Viewer — live + playback</option>
                <option value="operator">Operator — + PTZ / export</option>
                <option value="auditor">Auditor — audit log + evidence</option>
                <option value="admin">Admin — full tenant access</option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                The invitee's base role. Pick <strong>Admin</strong> for a tenant
                administrator who manages cameras and users.
              </p>
            </div>
            <div>
              <Label>Entity (optional)</Label>
              <Input
                placeholder="Entity UUID (leave blank for global scope)"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Paste the entity ID for now. The full entity picker is on
                the user detail page after invite.
              </p>
            </div>
            {entityId && (
              <div className="space-y-2">
                <Label>Scope</Label>
                <div className="space-y-1 text-sm">
                  <label className="flex items-start gap-2">
                    <input
                      type="radio"
                      checked={scope === "subtree"}
                      onChange={() => setScope("subtree")}
                    />
                    <span>
                      <strong>Subtree</strong> — access to the selected
                      entity and all descendants.
                    </span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input
                      type="radio"
                      checked={scope === "exact"}
                      onChange={() => setScope("exact")}
                    />
                    <span>
                      <strong>Exact</strong> — access to the selected
                      entity only.
                    </span>
                  </label>
                </div>
              </div>
            )}
            {error && (
              <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" asChild>
                <Link to="/users">Cancel</Link>
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? "Sending…" : "Send Invitation"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
